import React, {useCallback, useEffect, useState} from 'react';
import {
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  TextInput,
  View,
} from 'react-native';
import CCText from '@/Atoms/CCText';
import CCLoader from '@/Atoms/CCLoader';
import ErrorBanner from '@/Components/ErrorBanner';
import useScheduleClubTournament from '@/Hooks/ClubsApi/useScheduleClubTournament';
import {useCreateClubEvent} from '@/Hooks/ClubsApi/useClubsQueries';
import {useMediaQuery} from '@/Hooks/useMediaQuery/useMediaQuery';
import {Colors} from '@/styles';

type ScheduleTab = 'tournament' | 'puzzle';
type ClubTournamentType = 'Circlechess_Online' | 'Circlechess_Arena';

interface ScheduleModalProps {
  clubId: number;
  clubName: string;
  visible: boolean;
  onClose: () => void;
}

const TIME_CONTROLS = ['3+2', '5+0', '10+0', '15+10'];
const SWISS_ROUNDS = ['3', '5', '7', '9'];
const ARENA_DURATIONS = ['30', '60', '90', '120'];
const PUZZLE_GOALS = ['500', '1000', '2500', '5000'];

const formatDateInputValue = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    '0',
  )}-${String(date.getDate()).padStart(2, '0')}`;

const parseDateInputValue = (value: string) => {
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) {
    return null;
  }
  const result = new Date(year, month - 1, day);
  return Number.isNaN(result.getTime()) ? null : result;
};

const dateFromToday = (days: number) => {
  const result = new Date();
  result.setHours(0, 0, 0, 0);
  result.setDate(result.getDate() + days);
  return result;
};

const inputStyle = {
  borderWidth: 1,
  borderColor: 'rgba(38,35,34,0.16)',
  borderRadius: 13,
  paddingHorizontal: 13,
  paddingVertical: 12,
  fontSize: 14,
  color: Colors.fontDark,
  backgroundColor: Colors.white,
  ...(Platform.OS === 'web' ? ({outlineStyle: 'none'} as any) : null),
};

const webPickerInputStyle = {
  flex: 1,
  width: '100%',
  minWidth: 0,
  height: 44,
  border: 'none',
  backgroundColor: 'transparent',
  color: Colors.fontDark,
  fontFamily: 'inherit',
  fontSize: 13,
  fontWeight: 600,
  outline: 'none',
  cursor: 'pointer',
  colorScheme: 'light',
};

export default function ScheduleModal({
  clubId,
  clubName,
  visible,
  onClose,
}: ScheduleModalProps) {
  const {isMobileOrTab} = useMediaQuery();
  const isWide = !isMobileOrTab;
  const [tab, setTab] = useState<ScheduleTab>('tournament');
  const [tournamentType, setTournamentType] =
    useState<ClubTournamentType>('Circlechess_Online');
  const [name, setName] = useState('');
  const [tc, setTc] = useState('5+0');
  const [rounds, setRounds] = useState('5');
  const [duration, setDuration] = useState('60');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [startTime, setStartTime] = useState('18:00');
  const [tcError, setTcError] = useState('');
  const [generalError, setGeneralError] = useState('');
  const [psTitle, setPsTitle] = useState('');
  const [psGoal, setPsGoal] = useState('5000');
  const [psError, setPsError] = useState('');

  const scheduleTourney = useScheduleClubTournament();
  const createEvent = useCreateClubEvent(clubId);
  const isLoading =
    scheduleTourney.isLoading ||
    (scheduleTourney as any).isPending ||
    createEvent.isPending ||
    (createEvent as any).isLoading;

  const reset = () => {
    setTab('tournament');
    setTournamentType('Circlechess_Online');
    setName('');
    setTc('5+0');
    setRounds('5');
    setDuration('60');
    setStartDate(null);
    setStartTime('18:00');
    setTcError('');
    setGeneralError('');
    setPsTitle('');
    setPsGoal('5000');
    setPsError('');
  };

  useEffect(() => {
    if (!visible) {
      reset();
    }
  }, [visible]);

  const close = () => {
    reset();
    onClose();
  };

  const selectTab = (next: ScheduleTab) => {
    setTab(next);
    setGeneralError('');
    setTcError('');
    setPsError('');
  };

  const handleStartDateChange = useCallback((value: Date | null) => {
    setStartDate(value);
    setGeneralError('');
  }, []);

  const selectStartTime = (value: string) => {
    setStartTime(value);
    setGeneralError('');
  };

  const parseTc = (
    value: string,
  ): {
    initialTime: number;
    incrementTime: number;
    valid: boolean;
    error?: string;
  } => {
    const parts = value.split('+');
    if (parts.length !== 2) {
      return {
        initialTime: 0,
        incrementTime: 0,
        valid: false,
        error: 'Use minutes + increment, for example 5+0.',
      };
    }
    const minutes = Number(parts[0].trim());
    const increment = Number(parts[1].trim());
    if (
      !Number.isFinite(minutes) ||
      !Number.isFinite(increment) ||
      minutes < 0 ||
      increment < 0
    ) {
      return {
        initialTime: 0,
        incrementTime: 0,
        valid: false,
        error: 'Enter positive time values.',
      };
    }
    return {
      initialTime: minutes * 60,
      incrementTime: increment,
      valid: true,
    };
  };

  const resolveStartDate = () => {
    if (!startDate) {
      setGeneralError('Choose a start date.');
      return null;
    }
    const parts = startTime.split(':');
    const hours = Number(parts[0]);
    const minutes = Number(parts[1]);
    if (
      parts.length !== 2 ||
      !Number.isFinite(hours) ||
      !Number.isFinite(minutes) ||
      hours < 0 ||
      hours > 23 ||
      minutes < 0 ||
      minutes > 59
    ) {
      setGeneralError('Enter the start time as HH:MM, such as 18:00.');
      return null;
    }
    const result = new Date(startDate);
    result.setHours(hours, minutes, 0, 0);
    if (result.getTime() <= Date.now()) {
      setGeneralError('Choose a start time in the future.');
      return null;
    }
    return result;
  };

  const handleTournamentSubmit = async () => {
    setGeneralError('');
    setTcError('');

    if (!name.trim()) {
      setGeneralError('Give your tournament a name.');
      return;
    }
    const parsedTc = parseTc(tc);
    if (!parsedTc.valid) {
      setTcError(parsedTc.error ?? 'Enter a valid time control.');
      return;
    }
    const roundsNumber = Number(rounds);
    const durationNumber = Number(duration);
    if (
      tournamentType === 'Circlechess_Online' &&
      (!Number.isFinite(roundsNumber) || roundsNumber < 1)
    ) {
      setGeneralError('Choose at least one Swiss round.');
      return;
    }
    if (
      tournamentType === 'Circlechess_Arena' &&
      (!Number.isFinite(durationNumber) ||
        durationNumber < 10 ||
        durationNumber > 720)
    ) {
      setGeneralError('Arena duration must be between 10 and 720 minutes.');
      return;
    }
    const startsAt = resolveStartDate();
    if (!startsAt) {
      return;
    }

    try {
      const input: any = {
        clubId,
        type: tournamentType,
        name: name.trim(),
        organizer: clubName,
        initialTime: parsedTc.initialTime,
        incrementTime: parsedTc.incrementTime,
        startDate: startsAt.toISOString(),
      };
      if (tournamentType === 'Circlechess_Online') {
        input.rounds = roundsNumber;
      } else {
        input.durationMinutes = durationNumber;
      }
      await scheduleTourney.mutateAsync(input);
      close();
    } catch (error: any) {
      setGeneralError(error?.message || 'Could not schedule the tournament.');
    }
  };

  const handlePuzzleSubmit = async () => {
    setPsError('');
    const goal = Number(psGoal);
    if (!Number.isFinite(goal) || goal <= 0) {
      setPsError('Choose a positive puzzle goal.');
      return;
    }
    try {
      const startsAt = new Date();
      const endsAt = new Date(startsAt.getTime() + 7 * 86400000);
      await createEvent.mutateAsync({
        title: psTitle.trim() || 'Puzzle Storm',
        goal,
        starts_at: startsAt.toISOString(),
        ends_at: endsAt.toISOString(),
      });
      close();
    } catch (error: any) {
      setPsError(error?.message || 'Could not start Puzzle Storm.');
    }
  };

  const tcValid = parseTc(tc).valid;
  const tournamentReady =
    name.trim().length > 0 &&
    tcValid &&
    !!startDate &&
    /^\d{1,2}:\d{2}$/.test(startTime) &&
    (tournamentType === 'Circlechess_Online'
      ? Number(rounds) >= 1
      : Number(duration) >= 10 && Number(duration) <= 720);
  const puzzleReady = Number(psGoal) > 0;
  const isSwiss = tournamentType === 'Circlechess_Online';

  return (
    <Modal
      transparent
      animationType={isWide ? 'fade' : 'slide'}
      visible={visible}
      onRequestClose={close}>
      <Pressable
        testID="schedule-backdrop"
        style={[styles.backdrop, isWide ? styles.center : styles.bottom]}
        onPress={close}>
        <Pressable
          testID="schedule-modal"
          style={[styles.sheet, isWide ? styles.card : styles.bottomSheet]}
          onPress={() => {}}>
          {!isWide ? <View style={styles.grabber} /> : null}
          {isLoading ? <CCLoader loading /> : null}

          <View style={styles.header}>
            <View style={styles.headerTop}>
              <View style={styles.headerIcon}>
                <CCText style={styles.headerIconText}>◷</CCText>
              </View>
              <Pressable
                testID="schedule-modal-close"
                accessibilityLabel="Close schedule"
                onPress={close}
                style={styles.closeButton}>
                <CCText style={styles.closeText}>×</CCText>
              </Pressable>
            </View>
            <CCText
              fontWeight="thunder"
              style={[styles.title, !isWide && styles.titleMobile]}>
              Plan a Club Activity
            </CCText>
            <CCText style={styles.subtitle}>
              {`Choose a tournament or Puzzle Storm to get ${clubName} playing together.`}
            </CCText>
          </View>

          <View style={styles.tabs}>
            <ModeTab
              testID="tab-tournament"
              icon="♜"
              title="Tournament"
              subtitle="Swiss or Arena"
              active={tab === 'tournament'}
              onPress={() => selectTab('tournament')}
            />
            <ModeTab
              testID="tab-puzzle"
              icon="⚡"
              title="Puzzle Storm"
              subtitle="7-day team goal"
              active={tab === 'puzzle'}
              onPress={() => selectTab('puzzle')}
            />
          </View>

          <ScrollView
            testID="schedule-scroll"
            style={styles.scroll}
            contentContainerStyle={styles.content}
            showsVerticalScrollIndicator
            nestedScrollEnabled
            keyboardShouldPersistTaps="handled">
            {tab === 'tournament' ? (
              <>
                <Step
                  number="1"
                  title="Choose a tournament format"
                  hint="Pick how club members compete.">
                  <View style={styles.choiceGrid}>
                    <ChoiceCard
                      testID="schedule-format-swiss"
                      icon="♟"
                      title="Swiss"
                      description="Fixed rounds. Everyone keeps playing."
                      selected={isSwiss}
                      onPress={() => setTournamentType('Circlechess_Online')}
                    />
                    <ChoiceCard
                      testID="schedule-format-arena"
                      icon="♞"
                      title="Arena"
                      description="Score as many wins as possible before time runs out."
                      selected={!isSwiss}
                      onPress={() => setTournamentType('Circlechess_Arena')}
                    />
                  </View>
                </Step>

                <Step
                  number="2"
                  title="Name the event"
                  hint="Make it easy to recognize in the club.">
                  <TextInput
                    testID="sched-name"
                    value={name}
                    onChangeText={value => {
                      setName(value);
                      setGeneralError('');
                    }}
                    placeholder="e.g. Friday Blitz Battle"
                    placeholderTextColor="#9A918A"
                    style={inputStyle as any}
                  />
                </Step>

                <Step
                  number="3"
                  title="Set the game rules"
                  hint="Choose a clock and event length.">
                  <CCText style={styles.fieldLabel}>Time control</CCText>
                  <View style={styles.presetRow}>
                    {TIME_CONTROLS.map(value => (
                      <Preset
                        key={value}
                        label={value}
                        selected={tc === value}
                        onPress={() => {
                          setTc(value);
                          setTcError('');
                        }}
                      />
                    ))}
                  </View>
                  <TextInput
                    testID="sched-tc"
                    value={tc}
                    onChangeText={value => {
                      setTc(value);
                      setTcError('');
                    }}
                    placeholder="Minutes + increment"
                    placeholderTextColor="#9A918A"
                    style={[inputStyle, styles.customInput] as any}
                  />
                  {tcError ? (
                    <ErrorBanner
                      testID="schedule-time-error"
                      message={tcError}
                      style={styles.inlineError}
                    />
                  ) : null}

                  <CCText style={styles.fieldLabel}>
                    {isSwiss ? 'Number of rounds' : 'Arena duration'}
                  </CCText>
                  <View style={styles.presetRow}>
                    {(isSwiss ? SWISS_ROUNDS : ARENA_DURATIONS).map(value => (
                      <Preset
                        key={value}
                        label={isSwiss ? value : `${value} min`}
                        selected={(isSwiss ? rounds : duration) === value}
                        onPress={() =>
                          isSwiss ? setRounds(value) : setDuration(value)
                        }
                      />
                    ))}
                  </View>
                  <TextInput
                    testID={isSwiss ? 'sched-rounds' : 'sched-duration'}
                    value={isSwiss ? rounds : duration}
                    onChangeText={value => {
                      const digits = value.replace(/\D/g, '');
                      isSwiss ? setRounds(digits) : setDuration(digits);
                    }}
                    keyboardType="number-pad"
                    placeholder={isSwiss ? 'Rounds' : 'Minutes'}
                    placeholderTextColor="#9A918A"
                    style={[inputStyle, styles.customInput] as any}
                  />
                </Step>

                <Step
                  number="4"
                  title="Choose when it starts"
                  hint="Pick a date and time—members will see it in their local schedule.">
                  <View style={styles.dateQuickPicks}>
                    <Preset
                      label="Tomorrow"
                      selected={
                        startDate
                          ? formatDateInputValue(startDate) ===
                            formatDateInputValue(dateFromToday(1))
                          : false
                      }
                      onPress={() => handleStartDateChange(dateFromToday(1))}
                    />
                    <Preset
                      label="This weekend"
                      selected={
                        startDate
                          ? formatDateInputValue(startDate) ===
                            formatDateInputValue(
                              dateFromToday(
                                (6 - new Date().getDay() + 7) % 7 || 7,
                              ),
                            )
                          : false
                      }
                      onPress={() =>
                        handleStartDateChange(
                          dateFromToday((6 - new Date().getDay() + 7) % 7 || 7),
                        )
                      }
                    />
                    <Preset
                      label="Next week"
                      selected={
                        startDate
                          ? formatDateInputValue(startDate) ===
                            formatDateInputValue(dateFromToday(7))
                          : false
                      }
                      onPress={() => handleStartDateChange(dateFromToday(7))}
                    />
                  </View>
                  <View style={styles.dateTimeGrid}>
                    <View style={styles.dateField}>
                      <CCText style={styles.fieldLabel}>Start date</CCText>
                      <View style={styles.pickerShell}>
                        <View style={styles.pickerIcon}>
                          <CCText style={styles.pickerIconText}>▣</CCText>
                        </View>
                        <input
                          data-testid="sched-date"
                          aria-label="Tournament start date"
                          type="date"
                          value={
                            startDate ? formatDateInputValue(startDate) : ''
                          }
                          min={formatDateInputValue(new Date())}
                          onChange={(event: any) =>
                            handleStartDateChange(
                              parseDateInputValue(event.target.value),
                            )
                          }
                          style={webPickerInputStyle as any}
                        />
                      </View>
                    </View>
                    <View style={styles.timeField}>
                      <CCText style={styles.fieldLabel}>Start time</CCText>
                      <View style={styles.pickerShell}>
                        <View style={styles.pickerIcon}>
                          <CCText style={styles.pickerIconText}>◷</CCText>
                        </View>
                        <input
                          data-testid="sched-time"
                          aria-label="Tournament start time"
                          type="time"
                          value={startTime}
                          step={300}
                          onChange={(event: any) =>
                            selectStartTime(event.target.value)
                          }
                          style={webPickerInputStyle as any}
                        />
                      </View>
                    </View>
                  </View>
                  <View style={styles.timeQuickPicks}>
                    <CCText style={styles.quickPickLabel}>Quick times</CCText>
                    <View style={styles.presetRow}>
                      {[
                        ['10:00', 'Morning · 10:00'],
                        ['14:00', 'Afternoon · 14:00'],
                        ['18:00', 'Evening · 18:00'],
                      ].map(([value, label]) => (
                        <Preset
                          key={value}
                          label={label}
                          selected={startTime === value}
                          onPress={() => selectStartTime(value)}
                        />
                      ))}
                    </View>
                  </View>
                  <CCText style={styles.selectedSchedule}>
                    {startDate
                      ? `Starts ${startDate.toLocaleDateString(undefined, {
                          weekday: 'long',
                          day: 'numeric',
                          month: 'long',
                        })} at ${startTime}`
                      : 'Choose a date to complete the schedule.'}
                  </CCText>
                </Step>

                <View style={styles.summary}>
                  <CCText style={styles.summaryEyebrow}>
                    TOURNAMENT PREVIEW
                  </CCText>
                  <CCText style={styles.summaryTitle}>
                    {name.trim() || 'Your club tournament'}
                  </CCText>
                  <CCText style={styles.summaryText}>
                    {`${
                      isSwiss
                        ? `Swiss · ${rounds || '—'} rounds`
                        : `Arena · ${duration || '—'} minutes`
                    } · ${tc || 'No clock'}`}
                  </CCText>
                </View>

                {generalError ? (
                  <ErrorBanner
                    testID="schedule-general-error"
                    message={generalError}
                    style={styles.error}
                  />
                ) : null}
              </>
            ) : (
              <>
                <View style={styles.puzzleIntro}>
                  <CCText style={styles.puzzleIcon}>⚡</CCText>
                  <View style={styles.puzzleIntroCopy}>
                    <CCText style={styles.puzzleIntroTitle}>
                      Rally the club around one goal
                    </CCText>
                    <CCText style={styles.puzzleIntroText}>
                      Every solved puzzle adds to the shared total. The event
                      starts now and stays open for seven days.
                    </CCText>
                  </View>
                </View>

                <Step
                  number="1"
                  title="Name the challenge"
                  hint="Optional — we will use Puzzle Storm if left empty.">
                  <TextInput
                    testID="ps-title"
                    value={psTitle}
                    onChangeText={setPsTitle}
                    placeholder="e.g. 5K Puzzle Marathon"
                    placeholderTextColor="#9A918A"
                    style={inputStyle as any}
                  />
                </Step>

                <Step
                  number="2"
                  title="Set the club goal"
                  hint="How many puzzles should everyone solve together?">
                  <View style={styles.goalGrid}>
                    {PUZZLE_GOALS.map(value => (
                      <Preset
                        key={value}
                        label={Number(value).toLocaleString()}
                        selected={psGoal === value}
                        onPress={() => {
                          setPsGoal(value);
                          setPsError('');
                        }}
                      />
                    ))}
                  </View>
                  <TextInput
                    testID="ps-goal"
                    value={psGoal}
                    onChangeText={value => {
                      setPsGoal(value.replace(/\D/g, ''));
                      setPsError('');
                    }}
                    keyboardType="number-pad"
                    placeholder="Custom puzzle goal"
                    placeholderTextColor="#9A918A"
                    style={[inputStyle, styles.customInput] as any}
                  />
                </Step>

                <View style={styles.summary}>
                  <CCText style={styles.summaryEyebrow}>
                    PUZZLE STORM PREVIEW
                  </CCText>
                  <CCText style={styles.summaryTitle}>
                    {psTitle.trim() || 'Puzzle Storm'}
                  </CCText>
                  <CCText style={styles.summaryText}>
                    {`${Number(
                      psGoal || 0,
                    ).toLocaleString()} puzzles · Starts now · 7 days`}
                  </CCText>
                </View>

                {psError ? (
                  <ErrorBanner
                    testID="schedule-puzzle-error"
                    message={psError}
                    style={styles.error}
                  />
                ) : null}
              </>
            )}
          </ScrollView>

          <View style={styles.footer}>
            <Pressable onPress={close} style={styles.cancelButton}>
              <CCText style={styles.cancelText}>Cancel</CCText>
            </Pressable>
            <Pressable
              testID={tab === 'tournament' ? 'sched-submit' : 'ps-submit'}
              disabled={
                isLoading ||
                (tab === 'tournament' ? !tournamentReady : !puzzleReady)
              }
              onPress={
                tab === 'tournament'
                  ? handleTournamentSubmit
                  : handlePuzzleSubmit
              }
              style={[
                styles.primaryButton,
                (isLoading ||
                  (tab === 'tournament' ? !tournamentReady : !puzzleReady)) &&
                  styles.primaryButtonDisabled,
              ]}>
              <CCText style={styles.primaryButtonText}>
                {isLoading
                  ? tab === 'tournament'
                    ? 'Scheduling…'
                    : 'Starting…'
                  : tab === 'tournament'
                  ? 'Schedule tournament'
                  : 'Start Puzzle Storm'}
              </CCText>
              {!isLoading ? (
                <CCText style={styles.primaryArrow}>→</CCText>
              ) : null}
            </Pressable>
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

function ModeTab({
  testID,
  icon,
  title,
  subtitle,
  active,
  onPress,
}: {
  testID: string;
  icon: string;
  title: string;
  subtitle: string;
  active: boolean;
  onPress: () => void;
}) {
  return (
    <Pressable
      testID={testID}
      onPress={onPress}
      style={[styles.modeTab, active && styles.modeTabActive]}>
      <CCText style={styles.modeIcon}>{icon}</CCText>
      <View style={styles.modeCopy}>
        <CCText style={[styles.modeTitle, active && styles.modeTitleActive]}>
          {title}
        </CCText>
        <CCText
          style={[styles.modeSubtitle, active && styles.modeSubtitleActive]}>
          {subtitle}
        </CCText>
      </View>
      {active ? (
        <View style={styles.modeCheck}>
          <CCText style={styles.modeCheckText}>✓</CCText>
        </View>
      ) : null}
    </Pressable>
  );
}

function Step({
  number,
  title,
  hint,
  children,
}: {
  number: string;
  title: string;
  hint: string;
  children: React.ReactNode;
}) {
  return (
    <View style={styles.step}>
      <View style={styles.stepHeading}>
        <View style={styles.stepBadge}>
          <CCText style={styles.stepNumber}>{number}</CCText>
        </View>
        <View style={styles.stepCopy}>
          <CCText style={styles.stepTitle}>{title}</CCText>
          <CCText style={styles.stepHint}>{hint}</CCText>
        </View>
      </View>
      {children}
    </View>
  );
}

function ChoiceCard({
  testID,
  icon,
  title,
  description,
  selected,
  onPress,
}: {
  testID: string;
  icon: string;
  title: string;
  description: string;
  selected: boolean;
  onPress: () => void;
}) {
  return (
    <Pressable
      testID={testID}
      onPress={onPress}
      style={[styles.choiceCard, selected && styles.choiceCardSelected]}>
      <CCText style={styles.choiceIcon}>{icon}</CCText>
      <CCText
        style={[styles.choiceTitle, selected && styles.choiceTitleSelected]}>
        {title}
      </CCText>
      <CCText
        style={[
          styles.choiceDescription,
          selected && styles.choiceDescriptionSelected,
        ]}>
        {description}
      </CCText>
    </Pressable>
  );
}

function Preset({
  label,
  selected,
  onPress,
}: {
  label: string;
  selected: boolean;
  onPress: () => void;
}) {
  return (
    <Pressable
      onPress={onPress}
      style={[styles.preset, selected && styles.presetSelected]}>
      <CCText
        style={[styles.presetText, selected && styles.presetTextSelected]}>
        {label}
      </CCText>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(12,20,18,0.58)',
  },
  bottom: {
    justifyContent: 'flex-end',
  },
  center: {
    justifyContent: 'center',
    alignItems: 'center',
    padding: 28,
  },
  sheet: {
    width: '100%',
    height: '92%',
    maxHeight: '94%',
    overflow: 'hidden',
    backgroundColor: Colors.primaryLight,
  },
  bottomSheet: {
    borderTopLeftRadius: 28,
    borderTopRightRadius: 28,
  },
  card: {
    maxWidth: 580,
    height: '90%',
    borderRadius: 28,
  },
  grabber: {
    position: 'absolute',
    zIndex: 2,
    top: 9,
    width: 42,
    height: 4,
    borderRadius: 999,
    alignSelf: 'center',
    backgroundColor: 'rgba(255,255,255,0.38)',
  },
  header: {
    paddingHorizontal: 22,
    paddingTop: 20,
    paddingBottom: 20,
    backgroundColor: Colors.primaryDark,
  },
  headerTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 9,
  },
  headerIcon: {
    width: 38,
    height: 38,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Colors.primaryAction,
  },
  headerIconText: {
    color: Colors.fontDark,
    fontSize: 23,
  },
  closeButton: {
    width: 34,
    height: 34,
    borderRadius: 17,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.1)',
  },
  closeText: {
    color: Colors.white,
    fontSize: 24,
    lineHeight: 26,
  },
  title: {
    color: Colors.primaryLight,
    fontSize: 27,
    lineHeight: 31,
  },
  titleMobile: {
    fontSize: 33,
    lineHeight: 37,
  },
  subtitle: {
    color: '#DED5CA',
    fontSize: 13,
    lineHeight: 18,
    marginTop: 5,
  },
  tabs: {
    flexDirection: 'row',
    gap: 9,
    padding: 14,
    backgroundColor: Colors.white,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(38,35,34,0.1)',
  },
  modeTab: {
    flex: 1,
    minWidth: 0,
    minHeight: 62,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
    padding: 10,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: 'rgba(38,35,34,0.12)',
    backgroundColor: Colors.white,
  },
  modeTabActive: {
    borderColor: Colors.secondaryAction,
    backgroundColor: 'rgba(61,171,158,0.1)',
  },
  modeIcon: {
    fontSize: 19,
  },
  modeCopy: {
    flex: 1,
    minWidth: 0,
  },
  modeTitle: {
    color: Colors.fontDark,
    fontSize: 12.5,
    fontWeight: '700',
  },
  modeTitleActive: {
    color: '#276E66',
  },
  modeSubtitle: {
    color: '#8B827C',
    fontSize: 9.5,
    marginTop: 2,
  },
  modeSubtitleActive: {
    color: '#4B817A',
  },
  modeCheck: {
    width: 18,
    height: 18,
    borderRadius: 9,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Colors.secondaryAction,
  },
  modeCheckText: {
    color: Colors.white,
    fontSize: 10,
    fontWeight: '900',
  },
  scroll: {
    flex: 1,
    minHeight: 0,
  },
  content: {
    padding: 20,
    paddingBottom: 14,
  },
  step: {
    marginBottom: 26,
  },
  stepHeading: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 12,
  },
  stepBadge: {
    width: 27,
    height: 27,
    borderRadius: 9,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(61,171,158,0.13)',
  },
  stepNumber: {
    color: '#26766C',
    fontSize: 12,
    fontWeight: '800',
  },
  stepCopy: {
    flex: 1,
  },
  stepTitle: {
    color: Colors.fontDark,
    fontSize: 14.5,
    fontWeight: '700',
  },
  stepHint: {
    color: '#8B827C',
    fontSize: 11.5,
    marginTop: 1,
  },
  choiceGrid: {
    flexDirection: 'row',
    gap: 9,
  },
  choiceCard: {
    flex: 1,
    minWidth: 0,
    padding: 13,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: 'rgba(38,35,34,0.13)',
    backgroundColor: Colors.white,
  },
  choiceCardSelected: {
    borderColor: Colors.secondaryAction,
    backgroundColor: Colors.secondaryAction,
  },
  choiceIcon: {
    fontSize: 21,
    marginBottom: 8,
  },
  choiceTitle: {
    color: Colors.fontDark,
    fontSize: 13,
    fontWeight: '700',
  },
  choiceTitleSelected: {
    color: Colors.white,
  },
  choiceDescription: {
    color: '#817872',
    fontSize: 10.5,
    lineHeight: 14,
    marginTop: 4,
  },
  choiceDescriptionSelected: {
    color: 'rgba(255,255,255,0.82)',
  },
  fieldLabel: {
    color: Colors.primaryDark,
    fontSize: 11.5,
    fontWeight: '700',
    marginBottom: 7,
  },
  presetRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 7,
  },
  preset: {
    paddingHorizontal: 13,
    paddingVertical: 9,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: 'rgba(38,35,34,0.13)',
    backgroundColor: Colors.white,
  },
  presetSelected: {
    borderColor: Colors.secondaryAction,
    backgroundColor: Colors.secondaryAction,
  },
  presetText: {
    color: Colors.primaryDark,
    fontSize: 11.5,
    fontWeight: '600',
  },
  presetTextSelected: {
    color: Colors.white,
  },
  customInput: {
    marginTop: 9,
    marginBottom: 16,
  },
  inlineError: {
    marginTop: 7,
    marginBottom: 12,
  },
  dateTimeGrid: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
  },
  dateQuickPicks: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 7,
    marginBottom: 12,
  },
  dateField: {
    flex: 1.35,
    minWidth: 0,
  },
  timeField: {
    flex: 0.65,
    minWidth: 100,
  },
  pickerShell: {
    minHeight: 48,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
    paddingHorizontal: 10,
    borderWidth: 1,
    borderColor: 'rgba(38,35,34,0.16)',
    borderRadius: 13,
    backgroundColor: Colors.white,
  },
  pickerIcon: {
    width: 28,
    height: 28,
    borderRadius: 9,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(61,171,158,0.12)',
  },
  pickerIconText: {
    color: Colors.secondaryAction,
    fontSize: 15,
    fontWeight: '700',
  },
  timeQuickPicks: {
    marginTop: 10,
  },
  quickPickLabel: {
    color: '#817872',
    fontSize: 10.5,
    fontWeight: '600',
    marginBottom: 7,
  },
  selectedSchedule: {
    color: '#31796F',
    fontSize: 11.5,
    fontWeight: '600',
    lineHeight: 16,
    marginTop: 10,
  },
  summary: {
    padding: 15,
    borderRadius: 15,
    borderWidth: 1,
    borderColor: 'rgba(250,207,71,0.48)',
    backgroundColor: 'rgba(250,207,71,0.18)',
    marginBottom: 8,
  },
  summaryEyebrow: {
    color: '#80651C',
    fontSize: 8.5,
    fontWeight: '900',
    letterSpacing: 0.7,
  },
  summaryTitle: {
    color: Colors.fontDark,
    fontSize: 14,
    fontWeight: '700',
    marginTop: 4,
  },
  summaryText: {
    color: '#6F5D34',
    fontSize: 11.5,
    marginTop: 3,
  },
  puzzleIntro: {
    flexDirection: 'row',
    gap: 11,
    padding: 14,
    borderRadius: 15,
    backgroundColor: 'rgba(61,171,158,0.1)',
    borderWidth: 1,
    borderColor: 'rgba(61,171,158,0.25)',
    marginBottom: 24,
  },
  puzzleIcon: {
    fontSize: 22,
  },
  puzzleIntroCopy: {
    flex: 1,
  },
  puzzleIntroTitle: {
    color: '#286D65',
    fontSize: 13,
    fontWeight: '700',
  },
  puzzleIntroText: {
    color: '#57756F',
    fontSize: 11.5,
    lineHeight: 16,
    marginTop: 3,
  },
  goalGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 7,
  },
  error: {
    marginTop: 12,
  },
  footer: {
    flexDirection: 'row',
    gap: 10,
    paddingHorizontal: 20,
    paddingTop: 13,
    paddingBottom: 20,
    backgroundColor: Colors.white,
    borderTopWidth: 1,
    borderTopColor: 'rgba(38,35,34,0.1)',
  },
  cancelButton: {
    paddingHorizontal: 18,
    paddingVertical: 13,
    borderRadius: 999,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cancelText: {
    color: Colors.primaryDark,
    fontSize: 14,
    fontWeight: '600',
  },
  primaryButton: {
    flex: 1,
    minHeight: 47,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 9,
    paddingHorizontal: 18,
    paddingVertical: 13,
    borderRadius: 999,
    backgroundColor: Colors.secondaryAction,
  },
  primaryButtonDisabled: {
    backgroundColor: '#A8CEC8',
  },
  primaryButtonText: {
    color: Colors.white,
    fontSize: 14,
    fontWeight: '700',
  },
  primaryArrow: {
    color: Colors.white,
    fontSize: 18,
  },
});
